import { BookingWizardFlowTypes } from 'common/dist/infrastructure/modules/appointment/interfaces/AppointmentTypes';
import { useLocation, useNavigate } from 'react-router';
import { useEffect, useMemo } from 'react';

import { IBookingWizardNavigationHook } from 'application/modules/bookingWizard/useCases/hooks/useCaseNavigateBookingWizard';
import { WizardStep } from 'domain/entities/WizardStep';
import { selectAccessToken } from 'infrastructure/redux/slices/auth.selector';
import { selectExperienceType } from 'infrastructure/redux/slices/bookingWizard.selector';
import { useAppDispatch, useAppSelector } from 'infrastructure/redux/store/hooks';
import {
  bookingWizardAuthenticatedClubFittingByCoachFlowStepsOrder,
  bookingWizardAuthenticatedClubFittingFlowStepsOrder,
  bookingWizardAuthenticatedInstructorFlowStepsOrder,
  bookingWizardAuthenticatedSimulatorFlowStepsOrder,
  bookingWizardClubFittingByCoachFlowStepsOrder,
  bookingWizardClubFittingFlowStepsOrder,
  bookingWizardInstructorFlowStepsOrder,
  bookingWizardSimulatorFlowStepsOrder,
} from 'infrastructure/targets/web/modules/bookingWizard/navigation/BookingWizardStepsOrder';
import { pushToHistory } from 'infrastructure/redux/slices/navigationHistory.slice';

const GetWizardStepsOrder = () => {
  const experienceType = useAppSelector(selectExperienceType);
  const accessToken = useAppSelector(selectAccessToken);
  const isSignedIn = !!accessToken;

  if (!experienceType) {
    return bookingWizardSimulatorFlowStepsOrder;
  }
  switch (experienceType) {
    case BookingWizardFlowTypes.Lesson:
    case BookingWizardFlowTypes.LessonByCoach:
    case BookingWizardFlowTypes.LessonByDate:
      if (isSignedIn) {
        return bookingWizardAuthenticatedInstructorFlowStepsOrder;
      } else {
        return bookingWizardInstructorFlowStepsOrder;
      }
    case BookingWizardFlowTypes.FirstLesson:
      if (isSignedIn) {
        return bookingWizardAuthenticatedInstructorFlowStepsOrder;
      } else {
        return bookingWizardInstructorFlowStepsOrder;
      }
    case BookingWizardFlowTypes.Simulator:
    case BookingWizardFlowTypes.Bowling:
      if (isSignedIn) {
        return bookingWizardAuthenticatedSimulatorFlowStepsOrder;
      } else {
        return bookingWizardSimulatorFlowStepsOrder;
      }
    case BookingWizardFlowTypes.ClubFitting:
      if (isSignedIn) {
        return bookingWizardAuthenticatedClubFittingFlowStepsOrder;
      } else {
        return bookingWizardClubFittingFlowStepsOrder;
      }
    case BookingWizardFlowTypes.ClubFittingbyCoach:
      if (isSignedIn) {
        return bookingWizardAuthenticatedClubFittingByCoachFlowStepsOrder;
      } else {
        return bookingWizardClubFittingByCoachFlowStepsOrder;
      }
  }
};

const useBookingWizardNavigation: IBookingWizardNavigationHook = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const memoizedLocation = useMemo(() => location.pathname, [location.pathname]);
  const bookingWizardStepsOrder = GetWizardStepsOrder() || [];

  const handleRedirectToNextStep = (currentStep: WizardStep) => {
    const currentStepIndex = bookingWizardStepsOrder.findIndex((step) => step === currentStep);
    const nextStepIndex = currentStepIndex + 1;
    if (currentStepIndex === -1 || !bookingWizardStepsOrder[nextStepIndex]) {
      return;
    }
    navigate(`/${bookingWizardStepsOrder[nextStepIndex]}`);
  };

  const handleRedirectToPrevStep = (currentStep: WizardStep) => {
    const currentStepIndex = bookingWizardStepsOrder.findIndex((step) => step === currentStep);
    const prevStepIndex = currentStepIndex - 1;
    if (currentStepIndex === -1 || !bookingWizardStepsOrder[prevStepIndex]) {
      return;
    }
    navigate(`/${bookingWizardStepsOrder[prevStepIndex]}`);
  };

  useEffect(() => {
    // Save the current location to navigation history without the leading slash
    const pathWithoutLeadingSlash = memoizedLocation.startsWith('/')
      ? memoizedLocation.substring(1)
      : memoizedLocation;
    dispatch(pushToHistory(pathWithoutLeadingSlash));
  }, [memoizedLocation, dispatch]);

  return {
    redirectToNextStep: handleRedirectToNextStep,
    redirectToPrevStep: handleRedirectToPrevStep,
    redirectToStep: (step: WizardStep) => navigate(`/${step}`),
    redirectBack: () => navigate(-1),
  };
};

export default useBookingWizardNavigation;
